(() => {
  'use strict';

  /**
   * Nip2Lip Assist — store shopping assistant (live catalog + policies)
   * Architecture:
   *  Store  — live Shopify catalog facts (never invent)
   *  Memory — session conversation context
   *  Brain  — intent routing + composed replies
   *  UI     — compact panel; no duplicate actions
   */

  const root = document.querySelector('[data-n2l-assist]');
  const dataEl = document.getElementById('n2l-assist-data');
  if (!root || !dataEl || root.dataset.n2lAssistBound === 'true') return;
  root.dataset.n2lAssistBound = 'true';

  let catalog;
  try {
    catalog = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  const panel = root.querySelector('#n2l-assist-panel');
  const toggleBtn = root.querySelector('[data-n2l-assist-toggle]');
  const closeBtn = root.querySelector('[data-n2l-assist-close]');
  const messagesEl = root.querySelector('[data-n2l-assist-messages]');
  const quickEl = root.querySelector('[data-n2l-assist-quick]');
  const form = root.querySelector('[data-n2l-assist-form]');
  const input = root.querySelector('[data-n2l-assist-input]');
  const sendBtn = root.querySelector('[data-n2l-assist-send]');

  const KEYS = { name: 'n2l_assist_name', focus: 'n2l_assist_focus', hist: 'n2l_assist_hist' };

  /* ========================================================================
   * STORE — live catalog
   * ======================================================================== */
  const Store = (() => {
    const products = Array.isArray(catalog.products) ? catalog.products.slice() : [];

    const hydratePage = () => {
      const cp = catalog.page && catalog.page.product;
      if (!cp) return null;
      const hit = products.find((p) => p.id === cp.id || p.handle === cp.handle);
      if (hit) return hit;
      const tags = cp.tags || [];
      let status = 'available';
      if (tags.includes('preorder') || tags.includes('pre-order') || tags.includes('n2l-preorder')) status = 'preorder';
      else if (tags.includes('coming-soon') || tags.includes('n2l-coming-soon')) status = 'coming';
      else if (tags.some((t) => /archiv|sold-out|sold_out/i.test(t))) status = 'archive';
      else if (!cp.available) status = 'soldout';
      const sizes = [];
      (cp.variants || []).forEach((v) => {
        const label =
          (v.options && v.options.find((o) => /^(xxl|xl|xs|s|m|l|2xl)$/i.test(String(o)))) ||
          (v.options && v.options[0]) ||
          v.title;
        if (!label || /default/i.test(String(label))) return;
        let row = sizes.find((s) => String(s.label).toUpperCase() === String(label).toUpperCase());
        if (!row) sizes.push({ label: String(label), available: !!v.available });
        else if (v.available) row.available = true;
      });
      return { ...cp, status, drop: status === 'coming' ? 'Next drop' : status === 'archive' ? 'Archive' : 'Current drop', limited: tags.some((t) => /one-and-done/i.test(t)), colors: cp.colors || [], sizes };
    };

    const pageProduct = hydratePage();
    const money = (cents) => {
      if (typeof cents !== 'number') return '';
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: catalog.currency || 'USD', maximumFractionDigits: 0 }).format(cents / 100);
      } catch (e) {
        return `$${(cents / 100).toFixed(0)}`;
      }
    };
    const statusLabel = (p) => {
      if (!p) return '';
      if (p.status === 'coming') return 'Coming soon';
      if (p.status === 'preorder') return 'Pre-order';
      if (p.status === 'archive') return 'Sold out · Archive';
      if (p.status === 'soldout' || p.available === false) return 'Sold out';
      return 'Available';
    };
    const available = () => products.filter((p) => p.status === 'available' || (p.available && p.status !== 'coming' && p.status !== 'archive'));
    const coming = () => products.filter((p) => p.status === 'coming');
    const archive = () => products.filter((p) => p.status === 'archive' || p.status === 'soldout');
    const byId = (id) => products.find((p) => p.id === id) || (pageProduct && pageProduct.id === id ? pageProduct : null);
    const openSizes = (p) => (p.sizes || []).filter((s) => s.available).map((s) => s.label);
    const sizeEntry = (p, size) => {
      const want = String(size).toUpperCase().replace('2XL', 'XXL');
      return (p.sizes || []).find((s) => {
        const l = String(s.label).toUpperCase().replace('2XL', 'XXL');
        return l === want;
      });
    };
    const parseSize = (text) => {
      const m = String(text).match(/\b(2xl|xxl|xl|xs|s|m|l)\b/i);
      return m ? m[1].toUpperCase().replace('2XL', 'XXL') : null;
    };
    const find = (text) => {
      const q = String(text || '').toLowerCase();
      if (!q) return [];
      return products
        .map((p) => {
          const hay = `${p.title} ${p.handle} ${p.type || ''} ${(p.tags || []).join(' ')} ${p.description || ''} ${(p.colors || []).join(' ')}`.toLowerCase();
          let score = 0;
          if ((p.title || '').toLowerCase() === q) score += 100;
          if ((p.title || '').toLowerCase().includes(q)) score += 40;
          q.split(/\s+/).forEach((w) => {
            if (w.length > 2 && hay.includes(w)) score += 8;
          });
          // nickname boosts
          if (/take.?the.?shot/.test(q) && /take.?the.?shot/i.test(p.title)) score += 50;
          if (/day.?drinker/.test(q) && /day.?drinker/i.test(p.title)) score += 50;
          if (/bag.?of.?nips|all that/.test(q) && /bag.?of.?nips|all that/i.test(p.title)) score += 50;
          if (/hoodie/.test(q) && /hoodie/i.test(`${p.title} ${p.type}`)) score += 20;
          if (/tee|t-?shirt/.test(q) && /tee|t-?shirt|shirt/i.test(`${p.title} ${p.type}`)) score += 12;
          if (/black/.test(q) && /black/i.test(hay)) score += 10;
          return { p, score };
        })
        .filter((x) => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .map((x) => x.p);
    };
    const isTee = (p) => /tee|t-shirt|shirt/i.test(`${p.title} ${p.type || ''}`) && !/hoodie/i.test(p.title);
    const isHoodie = (p) => /hoodie/i.test(`${p.title} ${p.type || ''}`);
    const chooseBetween = (list) => {
      if (!list.length) return 'Nothing in the current drop is listed right now — check what’s coming next or join VIP.';
      if (list.length === 1)
        return `Available now: <strong>${list[0].title}</strong> — ${money(list[0].price)}, ${statusLabel(list[0]).toLowerCase()}.`;
      return `Currently available: <strong>${list[0].title}</strong> (${money(list[0].price)}) and <strong>${list[1].title}</strong>. Pick a piece below for sizes and details.`;
    };

    return {
      products,
      pageProduct,
      money,
      statusLabel,
      available,
      coming,
      archive,
      byId,
      openSizes,
      sizeEntry,
      parseSize,
      find,
      isTee,
      isHoodie,
      chooseBetween,
      urls: {
        shop: catalog.shopUrl,
        next: catalog.nextUrl,
        archive: catalog.archiveUrl,
        contact: catalog.contactUrl,
        vip: catalog.vipUrl,
        shipping: catalog.shippingUrl,
        returns: catalog.returnsUrl,
        orders: catalog.ordersUrl,
      },
      policies: catalog.policies || {},
      customerFirstName: catalog.customerFirstName || '',
    };
  })();

  /* Fashion / streetwear guidance — practical styling, store data for products */
  const Fashion = {
    stylePiece(product) {
      if (!product) {
        return 'Keep it New England streetwear: graphic tee, clean denim or cargos, low-profile sneakers. Layer a hoodie when it cools off.';
      }
      if (Store.isHoodie(product)) {
        return `Style <strong>${esc(product.title)}</strong> as the outer layer — dark denim, plain tee underneath, clean white or black sneakers.`;
      }
      return `Pair <strong>${esc(product.title)}</strong> with relaxed black or indigo denim and clean white sneakers. Add a hoodie when you need a layer.`;
    },
    sneakers() {
      return 'White leather lows or simple black sneakers. Keep the shoe quiet so the graphic stays loud.';
    },
    sizeAdvice(text) {
      if (/larger|oversized|bigger|roomy|baggy/.test(text || '')) {
        return 'For a roomier street fit, size up one if the blank runs true. Stay true to size for a cleaner cut.';
      }
      return 'Most Nip2Lip tees work true to size. Size up for oversized drape; stay true for a cleaner fit.';
    },
    outfitNE() {
      return 'New England formula: heavyweight graphic tee, black or washed denim, low sneakers, optional hoodie. Muted colors — black, cream, navy.';
    },
  };

  /* ========================================================================
   * MEMORY
   * ======================================================================== */
  const Memory = {
    history: [],
    focusId: null,
    lastListed: [],
    lastIntent: null,
    topic: null, // 'size' | 'shop' | 'coming' | ...

    load() {
      try {
        const f = sessionStorage.getItem(KEYS.focus);
        if (f) this.focusId = Number(f) || f;
      } catch (e) {
        /* ignore */
      }
    },
    setFocus(p) {
      if (!p) return;
      this.focusId = p.id;
      try {
        sessionStorage.setItem(KEYS.focus, String(p.id));
      } catch (e) {
        /* ignore */
      }
    },
    focus() {
      return (this.focusId && Store.byId(this.focusId)) || Store.pageProduct || null;
    },
    push(role, text) {
      this.history.push({ role, text: String(text || '').slice(0, 400) });
      if (this.history.length > 12) this.history.shift();
    },
    getName() {
      if (Store.customerFirstName) return Store.customerFirstName;
      try {
        return sessionStorage.getItem(KEYS.name) || '';
      } catch (e) {
        return '';
      }
    },
    setName(n) {
      const clean = String(n || '')
        .trim()
        .replace(/[^a-zA-Z\s'-]/g, '')
        .split(/\s+/)[0];
      if (!clean || clean.length < 2) return '';
      try {
        sessionStorage.setItem(KEYS.name, clean);
      } catch (e) {
        /* ignore */
      }
      return clean;
    },
  };
  Memory.load();

  /* ========================================================================
   * UI helpers
   * ======================================================================== */
  function esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function scrollBottom() {
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  function productCard(p, ctaLabel) {
    const sizes = Store.openSizes(p);
    const sizeLine =
      p.status === 'coming'
        ? 'Sizes open at drop'
        : sizes.length
          ? `Sizes: ${esc(sizes.join(' · '))}`
          : (p.sizes || []).length
            ? 'Sizes currently sold out'
            : '';
    const img = p.image
      ? `<img class="n2l-assist__card-img" src="${esc(p.image)}" alt="" loading="lazy" width="64" height="64">`
      : `<span class="n2l-assist__card-img n2l-assist__card-img--empty" aria-hidden="true"></span>`;
    const cta = ctaLabel || (p.status === 'coming' ? 'Get notified' : 'View product');
    return `<article class="n2l-assist__card">
      ${img}
      <div class="n2l-assist__card-body">
        <strong>${esc(p.title)}</strong>
        <span class="n2l-assist__card-status">${esc(Store.money(p.price))} · ${esc(Store.statusLabel(p))}</span>
        ${sizeLine ? `<span class="n2l-assist__card-meta">${sizeLine}</span>` : ''}
        <a class="n2l-assist__card-cta" href="${esc(p.url)}">${esc(cta)}</a>
      </div>
    </article>`;
  }

  /** Deduped action chips — never repeat labels already used in this HTML blob */
  function actions(list, usedLabels) {
    const used = new Set((usedLabels || []).map((x) => String(x).toLowerCase()));
    const out = [];
    (list || []).forEach((a) => {
      if (!a || !a.label) return;
      const key = String(a.label).toLowerCase();
      if (used.has(key)) return;
      used.add(key);
      if (a.href) out.push(`<a class="n2l-assist__action" href="${esc(a.href)}">${esc(a.label)}</a>`);
      else if (a.action) out.push(`<button type="button" class="n2l-assist__action" data-n2l-assist-action="${esc(a.action)}">${esc(a.label)}</button>`);
    });
    if (!out.length) return { html: '', used };
    return { html: `<div class="n2l-assist__actions">${out.join('')}</div>`, used };
  }

  function append(role, html) {
    const el = document.createElement('div');
    el.className = `n2l-assist__msg n2l-assist__msg--${role}`;
    el.innerHTML = html;
    messagesEl.appendChild(el);
    scrollBottom();
    return el;
  }

  function setBusy(busy) {
    state.busy = busy;
    input.disabled = busy;
    updateSend();
    quickEl.querySelectorAll('button').forEach((b) => {
      b.disabled = busy;
    });
  }

  function updateSend() {
    sendBtn.disabled = state.busy || !String(input.value || '').trim();
  }

  function setQuick(mode, items) {
    if (mode === 'hide') {
      quickEl.hidden = true;
      quickEl.innerHTML = '';
      return;
    }
    quickEl.hidden = false;
    quickEl.dataset.mode = mode || 'ctx';
    const map =
      items ||
      (mode === 'initial'
        ? [
            { action: 'shop', label: 'Shop the drop' },
            { action: 'sizes', label: 'Check sizes' },
            { action: 'coming', label: 'Next drop' },
            { action: 'ask', label: 'Ask a question' },
          ]
        : []);
    // Strip any action whose label already appears in the last bot message
    const lastBot = messagesEl.querySelector('.n2l-assist__msg--bot:last-child');
    const lastText = (lastBot && lastBot.textContent) || '';
    quickEl.innerHTML = map
      .filter((a) => !lastText.toLowerCase().includes(String(a.label).toLowerCase()))
      .map((a) => `<button type="button" data-n2l-assist-action="${esc(a.action)}">${esc(a.label)}</button>`)
      .join('');
    if (!quickEl.innerHTML.trim()) quickEl.hidden = true;
  }

  const state = { open: false, busy: false, greeted: false };

  /* ========================================================================
   * BRAIN — intent + compose
   * ======================================================================== */
  function resolveProduct(text) {
    const lower = String(text || '').toLowerCase();
    if (/\b(this|that|it|the tee|the hoodie|this tee|this hoodie|that one)\b/.test(lower)) {
      const f = Memory.focus();
      if (f) return f;
    }
    const hits = Store.find(lower);
    if (hits[0]) return hits[0];
    if (Memory.lastListed.length === 1) return Store.byId(Memory.lastListed[0]);
    return Memory.focus();
  }

  function classify(text) {
    const t = String(text || '').toLowerCase().trim();
    const size = Store.parseSize(t);
    if (/(?:my name is|i'm|i am|call me)\s+[a-z]/i.test(t)) return { intent: 'name' };
    if (/^(hi|hello|hey|yo)\b/.test(t)) return { intent: 'hello' };
    if (/ask a question|contact|email (you|us)|human|speak to|talk to/.test(t)) return { intent: 'contact' };
    if (/track|order status|my order/.test(t)) return { intent: 'orders' };
    if (/shipping|returns?|exchange|refund/.test(t)) return { intent: 'policy' };
    if (/\bvip\b|waitlist|early access|get notified/.test(t) && !size) return { intent: 'vip' };
    if (/one.?&.?done|limited release/.test(t)) return { intent: 'onedone' };
    if (/coming next|upcoming|next drop|what.?s coming|coming soon/.test(t)) return { intent: 'coming' };
    if (/sold.?out|archive|released before|past drop/.test(t)) return { intent: 'archive' };
    if (/shop the drop|current drop|what can i buy|what.?s available|available now|browse|recommend.*(tee|hoodie|piece)|which.*(tee|hoodie)/.test(t))
      return { intent: 'shop' };
    if (/outfit|style|wear with|pair with|sneaker|shoes?|fashion|night out|layering|streetwear look|what should i wear/.test(t))
      return { intent: 'style', product: resolveProduct(t) };
    if (/check sizes|what sizes|availability|in stock|oversized|regular fit|true to size|size up|size down|m or l|should i get/.test(t) || size)
      return { intent: 'sizes', product: resolveProduct(t), size };
    if (/how much|price|\$\d+/.test(t)) return { intent: 'price', product: resolveProduct(t) };
    if (/hoodie|tee|t-shirt|take the shot|day drinker|bag of nips|colorway|black tee|show me/.test(t))
      return { intent: 'product', product: resolveProduct(t) };
    if (/what about|and the|those|these|also/.test(t)) return { intent: 'followup', product: resolveProduct(t) };
    return { intent: 'general', product: resolveProduct(t) };
  }

  function reply(text) {
    const lower = text.toLowerCase();
    const cls = classify(text);
    Memory.lastIntent = cls.intent;
    Memory.push('user', text);

    // name capture
    const nm = lower.match(/(?:my name is|i'm|i am|call me)\s+([a-z][a-z'-]{1,20})/i);
    if (nm) Memory.setName(nm[1]);

    let html = '';
    let follow = [];
    let usedInMsg = [];

    const focusProduct = (p) => {
      if (p) {
        Memory.setFocus(p);
        Memory.lastListed = [p.id];
      }
    };

    switch (cls.intent) {
      case 'name': {
        const name = Memory.setName((nm && nm[1]) || '');
        html = `<p>${name ? `Nice to meet you, ${esc(name)}. ` : ''}What can I help you find?</p>`;
        follow = [
          { action: 'shop', label: 'Shop the drop' },
          { action: 'coming', label: 'Next drop' },
        ];
        break;
      }
      case 'hello': {
        const name = Memory.getName();
        html = `<p>${name ? `Hey ${esc(name)} 👋` : 'Hey 👋'} What can I help you find?</p>`;
        follow = [
          { action: 'shop', label: 'Shop the drop' },
          { action: 'sizes', label: 'Check sizes' },
        ];
        break;
      }
      case 'contact': {
        html = `<p>Ask me about products, sizes, drops, shipping, VIP, or orders. For anything else, Contact will get you an email reply.</p>`;
        follow = [{ href: Store.urls.contact, label: 'Contact Nip2Lip' }];
        break;
      }
      case 'orders': {
        html = `<p>Track orders from your Nip2Lip account after you sign in.</p>`;
        follow = [
          { href: Store.urls.orders, label: 'My orders' },
          { href: Store.urls.contact, label: 'Contact' },
        ];
        break;
      }
      case 'policy': {
        html = `<p><strong>Shipping</strong><br>${esc(Store.policies.shipping)}</p><p><strong>Returns</strong><br>${esc(Store.policies.returns)}</p>`;
        follow = [
          { href: Store.urls.shipping, label: 'Shipping' },
          { href: Store.urls.returns, label: 'Returns' },
        ];
        break;
      }
      case 'vip': {
        html = `<p>VIP gets early word on limited releases and pre-orders — first in, every drop.</p>`;
        follow = [{ href: Store.urls.vip, label: 'Join VIP' }];
        break;
      }
      case 'onedone': {
        html = `<p>${esc(Store.policies.oneDone)}</p>`;
        follow = [{ action: 'shop', label: 'Shop the drop' }];
        break;
      }
      case 'coming': {
        const list = Store.coming();
        Memory.lastListed = list.map((p) => p.id);
        if (!list.length) {
          html = `<p>No upcoming pieces are listed right now. VIP is the fastest way to hear first.</p>`;
          follow = [{ href: Store.urls.vip, label: 'Join VIP' }];
        } else {
          if (list[0]) focusProduct(list[0]);
          html = `<p>Here’s what’s coming next:</p>${list.map((p) => productCard(p, 'Get notified')).join('')}`;
          follow = [{ href: Store.urls.next, label: 'Next drop page' }];
        }
        Memory.topic = 'coming';
        break;
      }
      case 'archive': {
        const list = Store.archive();
        Memory.lastListed = list.map((p) => p.id);
        html = list.length
          ? `<p>Sold-out / archive:</p>${list.map((p) => productCard(p)).join('')}`
          : `<p>No archive pieces are listed right now.</p>`;
        follow = [
          { href: Store.urls.archive, label: 'View archive' },
          { action: 'shop', label: 'Shop the drop' },
        ];
        break;
      }
      case 'shop': {
        const list = Store.available();
        Memory.lastListed = list.map((p) => p.id);
        if (!list.length) {
          html = `<p>Nothing is listed as available right now.</p>`;
          follow = [
            { action: 'coming', label: 'Next drop' },
            { href: Store.urls.vip, label: 'Join VIP' },
          ];
        } else {
          focusProduct(list[0]);
          html = `<p>${Store.chooseBetween(list)}</p>${list.map((p) => productCard(p)).join('')}`;
          follow = [{ href: Store.urls.shop, label: 'Shop the drop' }];
        }
        Memory.topic = 'shop';
        break;
      }
      case 'sizes': {
        const p = cls.product || Memory.focus();
        const size = cls.size;
        if (!p) {
          const list = Store.available();
          html = `<p>Which piece? Here’s live availability:</p>${list
            .map((x) => {
              const s = Store.openSizes(x);
              return `<p><strong>${esc(x.title)}</strong><br>${s.length ? esc(s.join(' · ')) : 'No sizes open right now'}</p>`;
            })
            .join('')}${list.map((x) => productCard(x)).join('')}`;
          Memory.lastListed = list.map((x) => x.id);
          follow = [{ action: 'shop', label: 'Shop the drop' }];
          break;
        }
        focusProduct(p);
        if (size) {
          const entry = Store.sizeEntry(p, size);
          if (p.status === 'coming') {
            html = `<p><strong>${esc(p.title)}</strong> is coming soon — ${esc(size)} isn’t open yet.</p>${productCard(p, 'Get notified')}`;
            follow = [{ href: p.url, label: 'Get notified' }];
          } else if (entry && entry.available) {
            html = `<p>Yes — <strong>${esc(p.title)}</strong> is showing ${esc(size)} available.</p>${productCard(p)}`;
            follow = [{ href: p.url, label: 'View product' }];
          } else {
            const open = Store.openSizes(p);
            html = `<p><strong>${esc(p.title)}</strong> is ${esc(Store.statusLabel(p).toLowerCase())}, but ${esc(size)} isn’t available right now.${
              open.length ? ` Open: ${esc(open.join(' · '))}.` : ''
            }</p>${productCard(p)}`;
            follow = [
              { href: p.url, label: 'View product' },
              { action: 'shop', label: 'Shop the drop' },
            ];
          }
        } else {
          const open = Store.openSizes(p);
          html = `<p><strong>${esc(p.title)}</strong> — ${esc(Store.statusLabel(p))}</p><p>${
            p.status === 'coming'
              ? 'Sizes open when the drop goes live.'
              : open.length
                ? `Currently available: <strong>${esc(open.join(' · '))}</strong>`
                : 'No sizes are open on the storefront right now.'
          }</p>${productCard(p)}`;
          follow = [{ href: p.url, label: 'View product' }];
        }
        Memory.topic = 'size';
        break;
      }
      case 'redirect_style':
      case 'style': {
        const p = cls.product || Memory.focus() || Store.pageProduct;
        focusProduct(p);
        if (/sneaker|shoe/.test(lower)) {
          html = `<p>${Fashion.sneakers()}</p>`;
        } else if (/new england|pvd|outfit/.test(lower) && !p) {
          html = `<p>${Fashion.outfitNE()}</p>`;
        } else if (/size|fit|oversized|m or l/.test(lower)) {
          html = `<p>${Fashion.sizeAdvice(lower)}</p>`;
        } else {
          html = `<p>${Fashion.stylePiece(p)}</p>`;
        }
        if (p) {
          html += productCard(p);
          follow = [{ href: p.url, label: 'View product' }, { action: 'shop', label: 'Shop the drop' }];
        } else {
          follow = [{ action: 'shop', label: 'Shop the drop' }];
        }
        Memory.topic = 'style';
        break;
      }
      case 'price': {
        const p = cls.product || Memory.focus();
        if (p) {
          focusProduct(p);
          html = `<p><strong>${esc(p.title)}</strong> is ${esc(Store.money(p.price))}.</p>${productCard(p)}`;
          follow = [{ href: p.url, label: 'View product' }];
        } else {
          const m = lower.match(/\$?\s*(\d{2,3})/);
          if (m) {
            const cents = Number(m[1]) * 100;
            const list = Store.available().filter((x) => x.price === cents || (/under|less/.test(lower) && x.price <= cents));
            html = list.length ? `<p>Matching pieces:</p>${list.map((x) => productCard(x)).join('')}` : `<p>Nothing available at that price right now.</p>`;
            Memory.lastListed = list.map((x) => x.id);
            follow = [{ action: 'shop', label: 'Shop the drop' }];
          } else {
            html = `<p>Tell me which piece and I’ll pull the live price.</p>`;
            follow = [{ action: 'shop', label: 'Shop the drop' }];
          }
        }
        break;
      }
      case 'product': {
        const p = cls.product;
        if (p) {
          focusProduct(p);
          html = `<p><strong>${esc(p.title)}</strong> — ${esc(Store.money(p.price))} · ${esc(Store.statusLabel(p))}</p>`;
          if (p.description) html += `<p>${esc(p.description.slice(0, 180))}${p.description.length > 180 ? '…' : ''}</p>`;
          html += productCard(p);
          follow = [{ href: p.url, label: 'View product' }, { action: 'sizes', label: 'Check sizes' }];
        } else {
          const list = Store.available();
          html = `<p>Here’s what’s live right now:</p>${list.map((x) => productCard(x)).join('')}`;
          Memory.lastListed = list.map((x) => x.id);
          follow = [{ href: Store.urls.shop, label: 'Shop the drop' }];
        }
        break;
      }
      case 'followup': {
        const p = cls.product || Memory.focus();
        if (Memory.topic === 'size' && p) {
          focusProduct(p);
          const open = Store.openSizes(p);
          html = `<p>For <strong>${esc(p.title)}</strong>${open.length ? `: <strong>${esc(open.join(' · '))}</strong>` : ', no sizes are open right now'}.</p>${productCard(p)}`;
          follow = [{ href: p.url, label: 'View product' }];
        } else if (p) {
          focusProduct(p);
          html = `<p>Still on <strong>${esc(p.title)}</strong> — ${esc(Store.money(p.price))} · ${esc(Store.statusLabel(p))}.</p>${productCard(p)}`;
          follow = [{ href: p.url, label: 'View product' }];
        } else {
          html = `<p>I can help with products, sizes, drops, shipping, or VIP.</p>`;
          follow = [
            { action: 'shop', label: 'Shop the drop' },
            { action: 'coming', label: 'Next drop' },
          ];
        }
        break;
      }
      default: {
        const p = cls.product || Memory.focus() || Store.pageProduct;
        if (p) {
          focusProduct(p);
          html = `<p><strong>${esc(p.title)}</strong> — ${esc(Store.money(p.price))} · ${esc(Store.statusLabel(p))}.</p>${productCard(p)}`;
          follow = [
            { href: p.url, label: 'View product' },
            { action: 'sizes', label: 'Check sizes' },
          ];
        } else {
          html = `<p>I can help with Nip2Lip products, sizes, drops, shipping, and VIP — using live store info only.</p>`;
          follow = [
            { action: 'shop', label: 'Shop the drop' },
            { action: 'coming', label: 'Next drop' },
            { href: Store.urls.contact, label: 'Contact' },
          ];
        }
      }
    }

    // Dedup follow-ups against labels already present (e.g. product card CTAs)
    const htmlLower = html.toLowerCase();
    const already = new Set(usedInMsg.map((x) => String(x).toLowerCase()));
    ['view product', 'get notified', 'shop the drop', 'next drop'].forEach((label) => {
      if (htmlLower.includes(label)) already.add(label);
    });
    follow = follow.filter((a) => a && a.label && !already.has(String(a.label).toLowerCase()));

    const act = actions(follow, [...already]);
    html += act.html;
    append('bot', html);
    Memory.push('bot', html.replace(/<[^>]+>/g, ' ').slice(0, 200));

    // Contextual quick bar — only action chips not already shown in the reply
    const replyText = html.replace(/<[^>]+>/g, ' ').toLowerCase();
    const ctxQuick = follow
      .filter((a) => a.action && !replyText.includes(String(a.label).toLowerCase()))
      .map((a) => ({ action: a.action, label: a.label }))
      .slice(0, 3);
    if (ctxQuick.length) setQuick('ctx', ctxQuick);
    else setQuick('hide');
  }

  function handleUser(raw, { silentUser } = {}) {
    const text = String(raw || '').trim();
    if (!text || state.busy) return;
    if (!silentUser) append('user', `<p>${esc(text)}</p>`);
    setBusy(true);
    const typing = document.createElement('div');
    typing.className = 'n2l-assist__msg n2l-assist__msg--bot n2l-assist__msg--typing';
    typing.dataset.n2lTyping = 'true';
    typing.innerHTML = `<p class="n2l-assist__typing" aria-label="Typing"><span></span><span></span><span></span></p>`;
    messagesEl.appendChild(typing);
    scrollBottom();

    window.setTimeout(() => {
      try {
        typing.remove();
        reply(text);
      } catch (err) {
        typing.remove();
        append(
          'bot',
          `<p>I’m having trouble checking that right now. Try again in a moment.</p>${actions([
            { action: 'shop', label: 'Shop the drop' },
            { href: Store.urls.contact, label: 'Contact Nip2Lip' },
          ]).html}`
        );
        setQuick('hide');
      } finally {
        setBusy(false);
      }
    }, 260);
  }

  function runAction(action) {
    if (state.busy) return;
    const prompts = {
      shop: 'Shop the drop',
      sizes: 'Check sizes',
      coming: 'Next drop',
      ask: 'Ask a question',
      archive: 'What is sold out?',
    };
    if (action === 'ask') {
      append('user', `<p>Ask a question</p>`);
      append(
        'bot',
        `<p>Ask about products, sizes, drops, shipping, VIP, or orders.</p>${actions([{ href: Store.urls.contact, label: 'Contact Nip2Lip' }]).html}`
      );
      setQuick('hide');
      input.focus();
      return;
    }
    if (prompts[action]) handleUser(prompts[action]);
  }

  function greet() {
    const name = Memory.getName();
    const pageP = Store.pageProduct;
    let html = '';
    if (pageP) {
      Memory.setFocus(pageP);
      html = name
        ? `<p>Hey ${esc(name)} 👋 Looking at the <strong>${esc(pageP.title)}</strong>?<br>I can help with sizes, styling, or what’s next.</p>`
        : `<p>Hey 👋 Looking at the <strong>${esc(pageP.title)}</strong>?<br>I can help with sizes, styling, or what’s next.</p>`;
    } else if (name) {
      html = `<p>Hey ${esc(name)} 👋<br>Welcome back to Nip2Lip. What can I help you with?</p>`;
    } else {
      html = `<p>Hey 👋 Welcome to Nip2Lip.<br>What can I help you find?</p>`;
    }
    // No action chips in greeting — initial quick bar handles that (prevents duplicates)
    append('bot', html);
    setQuick('initial');
  }

  /* ========================================================================
   * Open / close
   * ======================================================================== */
  function openPanel() {
    state.open = true;
    panel.hidden = false;
    root.classList.add('is-open');
    toggleBtn.setAttribute('aria-expanded', 'true');
    if (!state.greeted) {
      greet();
      state.greeted = true;
    }
    window.setTimeout(() => {
      try {
        input.focus({ preventScroll: true });
      } catch (e) {
        input.focus();
      }
    }, 20);
  }

  function closePanel(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    state.open = false;
    panel.hidden = true;
    root.classList.remove('is-open');
    toggleBtn.setAttribute('aria-expanded', 'false');
  }

  closeBtn.addEventListener('click', closePanel);
  closeBtn.addEventListener('pointerdown', (e) => e.stopPropagation(), true);
  toggleBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (state.open) closePanel(e);
    else openPanel();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && state.open) closePanel(e);
  });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const v = input.value;
    input.value = '';
    updateSend();
    handleUser(v);
  });
  input.addEventListener('input', updateSend);
  quickEl.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-n2l-assist-action]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    runAction(btn.getAttribute('data-n2l-assist-action'));
  });
  messagesEl.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-n2l-assist-action]');
    if (!btn) return;
    e.preventDefault();
    runAction(btn.getAttribute('data-n2l-assist-action'));
  });

  updateSend();
})();
